/**
 * V69: getProjectWorkforceDashboard callable hook
 */
import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';

export interface MemberEntry {
  // 내부 직원
  userId?: string;
  nameKo?: string;
  orgUnitName?: string;
  companyCode?: 'KR' | 'CN';
  status?: 'active' | 'on_leave';
  leaveOverlapToday?: boolean;
  // 외부 인력 (V69)
  isExternal?: boolean;
  externalName?: string;
  externalOrgName?: string;
  // 공통
  grade: string;
  fte: number;
  role?: string;
  memo?: string;
  // V69 자동 비고
  remarks?: string;
}

export interface ProjectCard {
  projectId: string;
  projectName: string;
  ownerOrgUnitId: string;
  ownerOrgUnitName: string;
  companyCode: 'KR' | 'CN';
  effectiveFrom: string;
  contractStartDate?: string;
  contractEndDate?: string;
  contractNotes?: string;
  totalFte: number;          // 응답엔 유지 (FE 화면에서는 사용 안 함)
  krCount: number;
  cnCount: number;
  externalCount: number;
  duplicateCount: number;    // V69 polish 2: 본 프로젝트 members 중 동일 asOf active project 2+ 투입된 내부 직원 수
  members: MemberEntry[];
}

export interface CustomerGroup {
  customerId?: string;
  customerName: string;
  projects: ProjectCard[];
  // V69 polish 3: 고객사 내 dedupe 된 중복 투입 내부 직원 수
  duplicateMemberCount: number;
}

export interface WorkforceScope {
  countries: ('KR' | 'CN')[];
  canChooseAll: boolean;
}

export interface ProjectWorkforceDashboardResponse {
  success: boolean;
  asOf: string;
  scope: WorkforceScope;
  customers: CustomerGroup[];
  totals: {
    customers: number;
    projects: number;
    members: number;
    totalFte: number;
    duplicateMembers: number;  // V69 polish 4
  };
  error?: string;
}

export function useProjectWorkforceDashboard(asOf?: string) {
  const [data, setData] = useState<ProjectWorkforceDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const fn = httpsCallable<{ asOf?: string }, ProjectWorkforceDashboardResponse>(
          getFunctions(), 'getProjectWorkforceDashboard'
        );
        const r = await fn({ asOf });
        if (cancelled) return;
        if (r.data.success) {
          setData(r.data);
        } else {
          setError(r.data.error || '조회 실패');
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || '서버 오류');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [asOf]);

  return { data, loading, error };
}
